import groq from "groq";
import { FilterState } from "../types/filterManager";
import { Insight, insightFields } from "../types/insight";
import {
  getExcludeDraftsFilter,
  getFilterStateFilter,
} from "../queries/filters";
import { cmsQuery, CMSResult } from "./cmsClient";

type GetInsightsQueryArgs = {
  maxResults: number;
  preview?: boolean;
  filters?: FilterState;
};

type InsightParams = {
  // deliberately not optional because sanity needs these keys to be present in
  // the Params object; null because the params object must be valid JSON
  lastId: string | null;
  lastPublishedAt: string | null;
};

// response type for the query produced by getInsightsQuery
export type InsightsData = {
  insights: Insight[];
  numInsights: number;
};

export function getInsightsQuery({
  maxResults,
  preview,
  filters,
}: GetInsightsQueryArgs): string {
  // this query is broken into two steps (and two projections):
  //
  // the first filters the full set of documents by any non-pagination-related
  // filter criteria (whether to include drafts, whether user filters are
  // provided, etc.)
  // We do this first, in a separate projection, so we can store the filtered
  // result set for reuse in our second projection.
  //
  // the second projection does two things with this filtered result set:
  //
  // We paginate the results (see https://www.sanity.io/docs/paginating-with-groq)
  // We're ordering (and therefore paginating) by `lastPublishedAt`, with ties
  // broken by id. The decision to choose the higher id is arbitrary.
  //
  // We also count the number of results in our filtered subset. This is
  // potentially different from the number of results returned by our
  // pagination filter (if the page size < filtered result set size), which is
  // why this is broken into two parts.
  //
  return groq`
  {
    "filteredInsights": *[
      _type == "insight" 
      && ${getExcludeDraftsFilter(preview)} 
      && ${getFilterStateFilter(filters)}
    ]
    {
      ${insightFields}
    }
  }
  {
    "numInsights": count(filteredInsights),
    "insights": filteredInsights[ 
      (publishedAt < coalesce($lastPublishedAt, now())
        || (publishedAt == coalesce($lastPublishedAt, now())
            && _id > $lastId))
    ]
    | order(publishedAt desc)
    [0...${maxResults}]
  }
  `;
}

export type GetInsightsArgs = {
  lastInsight?: Insight;
  maxResults: number;
  preview?: boolean;
  filters?: FilterState;
};

export async function getInsights(
  args: GetInsightsArgs,
): Promise<CMSResult<InsightsData>> {
  const { lastInsight, maxResults, preview, filters } = args;
  let query = getInsightsQuery({ maxResults, preview, filters });

  const queryParams: InsightParams = {
    lastPublishedAt: lastInsight?.publishedAt ?? null,
    lastId: lastInsight?._id ?? null,
  };

  return cmsQuery<InsightsData>({
    query,
    queryParams,
    preview,
  });
}
